import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
} from 'react'

import { LoadingSpinner } from '../../../components'
import { EmptyState } from '../../../compositions'
import { ValkTalkPostForm } from '../../../containers/@forms/ValkTalkPostForm/ValkTalkPostForm'
import { ValkTalkPost, ValkTalkPostFormData } from '../../../entities/ValkTalkPost/ValkTalkPost'
import { getPermittedFeedTypes } from '../../../entities/ValkTalkPost/ValkTalkPostHelpers'
import { getPersonFullName } from '../../../helpers'
import usePermissions from '../../../helpers/hooks/usePermissions'
import trans from '../../../helpers/trans'
import { useTypedDispatch, useTypedSelector } from '../../../redux/store'
import { setToast } from '../../../redux/toasts/toastsReducer'
import { saveValkTalkPost } from '../../../redux/valkTalkPost/valkTalkPostActions'
import { setError, setIsSuccessful } from '../../../redux/valkTalkPost/valkTalkPostReducer'
import { ToastType } from '../../../types'
import { useValkTalkFeedsClient } from '../../ValkTalkFeedsClientProvider/ValkTalkFeedsClientProvider'

interface ConnectedValkTalkPostFormProps {
    post?: ValkTalkPost
    onSaved: () => void
    onClose: () => void
}

export const ConnectedValkTalkPostForm: FC<ConnectedValkTalkPostFormProps> = ({
    post,
    onSaved,
    onClose,
}): ReactElement => {
    const dispatch = useTypedDispatch()
    const permissions = usePermissions()
    const { user } = useTypedSelector(state => state.authenticatedUserReducer)
    const { isUpdating, isSuccessful, error } = useTypedSelector(state => state.valkTalkPostReducer)
    const { client, error: clientError } = useValkTalkFeedsClient()

    const permittedFeedTypes = useMemo(() => getPermittedFeedTypes(permissions), [permissions])
    const authorName = user ? getPersonFullName(user.person) : ''

    useEffect(() => () => {
        dispatch(setError(''))
    }, [])

    useEffect(() => {
        if (! isSuccessful) {
            return
        }

        dispatch(setIsSuccessful(false))
        dispatch(setToast({
            text: trans(post
                ? 'containers.forms.valkTalkPostForm.editPostSuccess'
                : 'containers.forms.valkTalkPostForm.addPostSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))

        onSaved()
    }, [isSuccessful])

    useEffect(() => {
        if (error) {
            dispatch(setToast({ text: error, type: ToastType.fail }))
            dispatch(setError(''))
        }
    }, [error])

    const handleSubmit = (formData: ValkTalkPostFormData): void => {
        if (client) {
            dispatch(saveValkTalkPost(client, formData, post?.id))
        }
    }

    if (clientError) {
        return (
            <EmptyState
                title={trans('containers.forms.valkTalkPostForm.errors.unavailableTitle')}
                intro={clientError}
            />
        )
    }

    if (! client) {
        return <LoadingSpinner />
    }

    return (
        <ValkTalkPostForm
            isLoading={isUpdating}
            authorName={authorName}
            permittedFeedTypes={permittedFeedTypes}
            post={post}
            onSubmit={handleSubmit}
            onCancel={onClose}
        />
    )
}
